import { useEffect, useState } from "react";
import api from "../../../../../utils/axiosInstance";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { useToast } from "../../../../../components/Toast/ToastProvider";
import { btn } from "../../../../../components/Button/buttonStyles";

const EditSpecializationsPage = ({ vendorId, onUpdateComplete }) => {
  const [specializations, setSpecializations] = useState([]);
  const [vendorSpecializations, setVendorSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all admin specializations
        const { data: allSpecsRes } = await api.get("/admin-specializations", {
          withCredentials: true,
        });

        // Fetch vendor specializations with mapping IDs
        const { data: vendorSpecsRes } = await api.get(
          `/vendor-specializations/${vendorId}/specializations`,
          { withCredentials: true }
        );

        const allSpecializations = allSpecsRes?.data || [];
        const vendorData = vendorSpecsRes?.data;

        let vendorSpecs = [];
        if (Array.isArray(vendorData?.specializations)) {
          vendorSpecs = vendorData.specializations;
        }

        console.log("All Specializations:", allSpecializations);
        console.log("Vendor Specializations:", vendorSpecs);

        setSpecializations(allSpecializations);
        setVendorSpecializations(vendorSpecs);
      } catch (error) {
        console.error("Error fetching specializations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);

  const isSpecializationSelected = (specId) =>
    vendorSpecializations.some((vs) => Number(vs.id) === Number(specId));

  const addSpecialization = async (specialization) => {
    try {
      setUpdating(true);
      const res = await api.post(
        "/vendor-specializations",
        { vendor_id: vendorId, specialization_id: specialization.id },
        { withCredentials: true }
      );

      const addedSpec = res.data.data;
      setVendorSpecializations((prev) => [
        ...prev,
        {
          id: specialization.id,
          name: specialization.name,
          mapping_id: addedSpec.id,
          price_count: 0,
        },
      ]);
    } catch (error) {
      console.error("Error adding vendor specialization:", error);
      toast.error(error.response?.data?.message || "Failed to add specialization");
    } finally {
      setUpdating(false);
    }
  };

  const removeSpecialization = async (specialization, vendorSpec) => {
    try {
      setUpdating(true);
      if (vendorSpec.mapping_id) {
        await api.delete(`/vendor-specializations/${vendorSpec.mapping_id}`, {
          withCredentials: true,
        });

        setVendorSpecializations((prev) =>
          prev.filter((vs) => Number(vs.id) !== Number(specialization.id))
        );
      }
    } catch (error) {
      console.error("Error removing vendor specialization:", error);
      toast.error(error.response?.data?.message || "Failed to remove specialization");
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckboxChange = (specialization) => {
    const selected = isSpecializationSelected(specialization.id);

    if (!selected) {
      addSpecialization(specialization);
      return;
    }

    const vendorSpec = vendorSpecializations.find(
      (vs) => Number(vs.id) === Number(specialization.id)
    );
    if (!vendorSpec) return;

    const priceCount = vendorSpec.price_count || 0;
    if (priceCount > 0) {
      setConfirm({
        title: "Remove Specialization?",
        message: `This will delete ${priceCount} price${priceCount === 1 ? "" : "s"} associated with "${specialization.name}". Continue?`,
        onConfirm: () => {
          setConfirm(null);
          removeSpecialization(specialization, vendorSpec);
        },
      });
      return;
    }

    removeSpecialization(specialization, vendorSpec);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <p className="text-gray-500 text-sm animate-pulse">Loading specializations...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                <th className="px-3 py-2 text-center font-semibold w-12">Select</th>
                <th className="px-3 py-2 text-center font-semibold">Specialization Name</th>
              </tr>
            </thead>
            <tbody>
              {specializations.length === 0 ? (
                <tr>
                  <td colSpan={2} className="py-10 text-center text-gray-400 text-xs">
                    No specializations available.
                  </td>
                </tr>
              ) : (
                specializations.map((spec, index) => {
                  const selected = isSpecializationSelected(spec.id);
                  const rowBg = selected
                    ? "bg-brand-50"
                    : index % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white";
                  return (
                    <tr
                      key={spec.id}
                      className={`${rowBg} hover:bg-brand-50 transition-colors`}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={updating}
                          onChange={() => handleCheckboxChange(spec)}
                          className="w-3.5 h-3.5 accent-brand-600 cursor-pointer disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-900">
                        {spec.name}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex mt-4">
        <button
          onClick={() => {
            if (typeof onUpdateComplete === "function") onUpdateComplete();
          }}
          disabled={updating}
          className={btn("primary", "sm")}
        >
          {updating ? "Updating..." : "Done"}
        </button>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
          confirmText="Continue"
        />
      )}
    </div>
  );
};

export default EditSpecializationsPage;
